// src/components/DynamicForms/DynamicForm.tsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getParticipantInfo } from "../../../services/participantServices/ParticipantService";
import {
  computeAllRequiredFields,
  validateFormData,
} from "./RuleEngine";
import { useAuth } from "../../../firebase/AuthProvider";
import { fetchDynamicOptions } from "./formsConfig";
import {
  FormAutoSaveRequestDto,
  FormConfig,
  FormInstanceDto,
  RulesConfig,
} from "../../../interfaces/dynamicForms/Forms";
import {
  AutoSaveFormAsync,
  GetInstanceDetailsAsync,
} from "../../../services/formsService/FormsService";
import { detailFetchers } from "../../../services/formsService/FormDetailFetcherSerivce";
import { VerticalStepBar } from "./DynamicFormComponents/VerticalStepBar";
import { StepPanel } from "./DynamicFormComponents/StepPanel";
import { SelectOption, isQuestionVisible } from "./DynamicFormComponents/utils";
import { getTaskById } from "../../../services/taskServices/TaskService";
import { getTaskTypes } from "../../../services/Lookups/TaskLookupService";

interface WizardFormProps {
  formConfig: FormConfig;
  rulesConfig: RulesConfig;
  back: () => void;
  onSubmit: (answers: Record<string, any>, instanceId: number) => void;
  participantId?: string;
  taskId?: string;
  autoPopulateData?: Record<string, any>;
}

type DynamicOptionsMap = Record<string, SelectOption[]>;

export const WizardForm: React.FC<WizardFormProps> = ({
  formConfig,
  rulesConfig,
  onSubmit,
  back,
  participantId,
  taskId,
  autoPopulateData,
}) => {
  const { currentUser } = useAuth();
  const { steps } = formConfig;
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dynamicOptions, setDynamicOptions] = useState<DynamicOptionsMap>({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formInstanceDetails, setFormInstanceDetails] =
    useState<FormInstanceDto>();
  const closeSidebar = () => setIsSidebarOpen(false);

  const getOrderedData = useCallback(
    (data: Record<string, any>): Record<string, any> => {
      const orderedData: Record<string, any> = {};
      const allQuestions = formConfig.steps.flatMap((step) => step.questions);

      allQuestions.forEach((q) => {
        if (Object.prototype.hasOwnProperty.call(data, q.id)) {
          orderedData[q.id] = data[q.id];
        }
      });

      Object.keys(data).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(orderedData, key)) {
          orderedData[key] = data[key];
        }
      });
      return orderedData;
    },
    [formConfig]
  );

  const allQuestions = useMemo(
    () => formConfig.steps.flatMap((step) => step.questions),
    [formConfig.steps]
  );

  const participantQuestionId = useMemo(() => {
    const participantQuestion = allQuestions.find(
      (q) => q.dynamicSource === "participants"
    );
    return participantQuestion?.id;
  }, [allQuestions]);


  const taskContactTypeQuestionId = useMemo(() => {
    let question = allQuestions.find(
      (q) => q.dynamicSource === "taskContactTypes"
    );
    if (!question) {
      question = allQuestions.find((q) =>
        q.label.toLowerCase().includes("contact type")
      );
    }
    return question?.id;
  }, [allQuestions]);

  useEffect(() => {
    if (
      participantId &&
      participantQuestionId &&
      dynamicOptions[participantQuestionId]
    ) {
      const participantOptions = dynamicOptions[participantQuestionId];
      const participantExists = participantOptions.some(
        (option) => option.value.toString() === participantId.toString()
      );

      if (participantExists) {
        if (formData[participantQuestionId] !== participantId.toString()) {
          setFormData((prev) => ({
            ...prev,
            [participantQuestionId]: participantId.toString(),
          }));
        }
      }
    }
  }, [participantId, participantQuestionId, dynamicOptions, formData]);

  // --- UPDATED EFFECT: Handle Contact Type (ID or Label) ---
  useEffect(() => {
    if (
      autoPopulateData?.contactType &&
      taskContactTypeQuestionId &&
      dynamicOptions[taskContactTypeQuestionId]
    ) {
      const options = dynamicOptions[taskContactTypeQuestionId];
      const incomingValue = autoPopulateData.contactType.toString();

      // 1. Try to find by Value (ID)
      let matchedOption = options.find(
        (option) => option.value.toString() === incomingValue
      );

      // 2. If not found, try to find by Label (e.g. "Interim")
      if (!matchedOption) {
        matchedOption = options.find(
          (option) => option.label.toLowerCase() === incomingValue.toLowerCase()
        );
      }

      if (matchedOption) {
        if (formData[taskContactTypeQuestionId] !== matchedOption.value) {
          setFormData((prev) => ({
            ...prev,
            [taskContactTypeQuestionId]: matchedOption!.value, // Set the ID
          }));
        }
      }
    }
  }, [autoPopulateData, taskContactTypeQuestionId, dynamicOptions, formData]);

  useEffect(() => {
    const ruleErrors = validateFormData(rulesConfig, formData);
    const localRequiredErrors = computeAllRequiredFields(steps, formData);
    const newErrors = { ...localRequiredErrors, ...ruleErrors };

    if (!shallowEqual(errors, newErrors)) {
      setErrors(newErrors);
    }
  }, [formData, rulesConfig, steps]);

  useEffect(() => {
    let changed;
    const cleanedData = { ...formData };

    do {
      changed = false;
      const dataForVisibilityCheck = { ...cleanedData };
      allQuestions.forEach((q) => {
        if (
          !isQuestionVisible(q, dataForVisibilityCheck) &&
          cleanedData[q.id] !== undefined
        ) {
          delete cleanedData[q.id];
          changed = true;
        }
      });
    } while (changed);

    if (!shallowEqual(formData, cleanedData)) {
      setFormData(cleanedData);
    }
  }, [formData, allQuestions]);

  function shallowEqual<T extends Record<string, any>>(
    objA: T,
    objB: T
  ): boolean {
    if (objA === objB) {
      return true;
    }

    if (
      !objA ||
      !objB ||
      typeof objA !== "object" ||
      typeof objB !== "object"
    ) {
      return false;
    }

    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (objA[key] !== objB[key]) {
        return false;
      }
    }

    return true;
  }

  useEffect(() => {
    let isMounted = true;

    async function loadAllDynamicSources() {
      const allQuestions = formConfig.steps.flatMap((step) => step.questions);
      for (const q of allQuestions) {
        if (q.dynamicSource && currentUser) {
          const authToken = await currentUser.getIdToken();

          if (q.dynamicSource === "taskContactTypes") {
            const taskTypesFromApi = await getTaskTypes(authToken);

            if (taskTypesFromApi && isMounted) {
              const formattedOptions = taskTypesFromApi.map((taskType) => ({
                value: taskType.id.toString(),
                label: taskType.type,
              }));

              setDynamicOptions((prev) => ({
                ...prev,
                [q.id]: formattedOptions,
              }));
            }
          } else {
            const data = await fetchDynamicOptions(q.dynamicSource, authToken);
            if (data && isMounted) {
              let transformedData = data;
              const transform = (q as any).optionLabelTransform;
              if (transform && transform.pattern) {
                const regex = new RegExp(transform.pattern, "g");
                transformedData = data.map((option: SelectOption) => ({
                  ...option,
                  label: option.label.replace(
                    regex,
                    transform.replacement || ""
                  ),
                }));
              }

              setDynamicOptions((prev) => ({
                ...prev,
                [q.id]: transformedData,
              }));
            }
          }
        }
      }
    }

    loadAllDynamicSources();
    return () => {
      isMounted = false;
    };
  }, [formConfig, currentUser]);

  const handleNext = async () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      if (Object.keys(errors).length > 0) {
        console.warn("Submission blocked due to validation errors:", errors);
        alert("Please fix the errors before submitting.");
        return;
      }

      if (!currentUser) {
        console.error("User not authenticated. Cannot submit.");
        alert("Authentication error. Please log in again.");
        return;
      }

      setIsSubmitting(true);
      try {
        const authToken = await currentUser.getIdToken();
        const enrichedFormData = { ...formData };
        const allQuestions = formConfig.steps.flatMap(
          (step) => step.questions
        );
        const fetchPromises: Promise<void>[] = [];

        allQuestions.forEach((q) => {
          if (q.dynamicSource && enrichedFormData[q.id] != null) {
            const source = q.dynamicSource;
            const selectedIdOrIds = enrichedFormData[q.id];

            if (!q.isMulti && typeof selectedIdOrIds === "string") {
              const promise = (async () => {
                try {
                  if (source === "participants") {
                    const fetchedData = await getParticipantInfo(
                      selectedIdOrIds,
                      authToken
                    );
                    if (fetchedData && fetchedData !== "unauthorized") {
                      enrichedFormData[q.id] = fetchedData;
                    }
                  }
                } catch (fetchError) {
                  console.error(
                    `Error fetching data for ${q.id} (ID: ${selectedIdOrIds}):`,
                    fetchError
                  );
                }
              })();
              fetchPromises.push(promise);
            } else if (
              q.isMulti &&
              Array.isArray(selectedIdOrIds) &&
              selectedIdOrIds.length > 0
            ) {
              console.warn(
                `Multi-select fetch-on-submit for ${q.id} requires a bulk fetch API.`
              );
            }
          }
        });

        await Promise.all(fetchPromises);

        allQuestions.forEach((q) => {
          const fieldId = q.id;
          if (
            q.type === "text" &&
            q.prefix &&
            typeof enrichedFormData[fieldId] === "string"
          ) {
            enrichedFormData[fieldId] = q.prefix + enrichedFormData[fieldId];
          }
        });

        const dataForSubmission = { ...enrichedFormData };
        allQuestions.forEach(q => {
          if ((q.dynamicSource === 'waiverTypes' || q.dynamicSource === 'taskContactTypes') && dataForSubmission[q.id]) {
            const questionId = q.id;
            const selectedIdOrIds = dataForSubmission[questionId];
            const options = dynamicOptions[questionId];

            if (options && options.length > 0) {
              if (q.isMulti && Array.isArray(selectedIdOrIds)) {
                const selectedLabels = selectedIdOrIds.map(id => {
                  const selectedOption = options.find(o => o.value.toString() === id.toString());
                  return selectedOption ? selectedOption.label : id;
                });
                dataForSubmission[questionId] = selectedLabels;
              } else if (!q.isMulti && (typeof selectedIdOrIds === 'string' || typeof selectedIdOrIds === 'number')) {
                const selectedOption = options.find(o => o.value.toString() === selectedIdOrIds.toString());
                if (selectedOption) {
                  dataForSubmission[questionId] = selectedOption.label;
                }
              }
            }
          }
        });

        isFinalSubmittingRef.current = true;
        onSubmit(getOrderedData(dataForSubmission), formInstanceDetails?.id || 0);
      } catch (error) {
        console.error("Error during submission data preparation:", error);
        alert(
          "An error occurred while preparing your submission. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  function handlePrev() {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }

  const handleInputChange = useCallback(
    (questionId: string) => (val: any) => {
      setFormData((prev) => ({ ...prev, [questionId]: val }));
    },
    []
  );

  const autoSavePdfRequest = useCallback(
    async (data: Record<string, any>, instanceId: number) => {
      if (!taskId || !participantId || !currentUser) return;

      try {
        const authToken = await currentUser.getIdToken();

        const transformedData = { ...data };
        allQuestions.forEach(q => {
          if ((q.dynamicSource === 'waiverTypes' || q.dynamicSource === 'taskContactTypes') && transformedData[q.id]) {
            const questionId = q.id;
            const selectedIdOrIds = transformedData[questionId];
            const options = dynamicOptions[questionId];

            if (options && options.length > 0) {
              if (q.isMulti && Array.isArray(selectedIdOrIds)) {
                const selectedLabels = selectedIdOrIds.map(id => {
                  const selectedOption = options.find(o => o.value.toString() === id.toString());
                  return selectedOption ? selectedOption.label : id;
                });
                transformedData[questionId] = selectedLabels;
              } else if (!q.isMulti && (typeof selectedIdOrIds === 'string' || typeof selectedIdOrIds === 'number')) {
                const selectedOption = options.find(o => o.value.toString() === selectedIdOrIds.toString());
                if (selectedOption) {
                  transformedData[questionId] = selectedOption.label;
                }
              }
            }
          }
        });

        const payload: FormAutoSaveRequestDto = {
          pdfTemplateName: formConfig.form.name,
          fields: getOrderedData(transformedData),
          isFinalSubmission: false,
          participantId: participantId.toString(),
          submittedBy: currentUser.uid,
          taskId: taskId,
          formInstanceId: instanceId,
        };
        await AutoSaveFormAsync(authToken, payload);
      } catch (err) {
        console.error("Auto-save error:", err);
      }
    },
    [currentUser, formConfig.form.name, participantId, taskId, getOrderedData]
  );

  const latestFormDataRef = useRef(formData);
  const latestAutoSavePdfRequestRef = useRef(autoSavePdfRequest);
  const latestFormInstanceDetailsRef = useRef(formInstanceDetails);
  const isFinalSubmittingRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const data = latestFormDataRef.current;
      const saveFn = latestAutoSavePdfRequestRef.current;
      const instanceId = latestFormInstanceDetailsRef.current?.id || 0;

      if (Object.keys(data).length > 0 && typeof saveFn === "function") {
        saveFn(data, instanceId);
      }

      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    latestFormDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    latestAutoSavePdfRequestRef.current = autoSavePdfRequest;
  }, [autoSavePdfRequest]);

  useEffect(() => {
    latestFormInstanceDetailsRef.current = formInstanceDetails;
  }, [formInstanceDetails]);

  useEffect(() => {
    return () => {
      if (isFinalSubmittingRef.current) return;
      const data = latestFormDataRef.current;
      const saveFn = latestAutoSavePdfRequestRef.current;
      const instanceId = latestFormInstanceDetailsRef.current?.id || 0;

      if (Object.keys(data).length > 0 && typeof saveFn === "function") {
        (async () => {
          await saveFn(data, instanceId);
        })();
      }
    };
  }, []);

  const cleanLoadedData = useCallback(
    (data: Record<string, any>): Record<string, any> => {
      const cleanedData = { ...data };

      allQuestions.forEach((q) => {
        const fieldId = q.id;
        if (
          q.type === "text" &&
          q.prefix &&
          typeof cleanedData[fieldId] === "string" &&
          cleanedData[fieldId].startsWith(q.prefix)
        ) {
          cleanedData[fieldId] = cleanedData[fieldId].substring(
            q.prefix.length
          );
        }
      });

      let changed;
      do {
        changed = false;
        const dataForVisibilityCheck = { ...cleanedData };
        allQuestions.forEach((q) => {
          if (
            !isQuestionVisible(q, dataForVisibilityCheck) &&
            cleanedData[q.id] !== undefined
          ) {
            delete cleanedData[q.id];
            changed = true;
          }
        });
      } while (changed);

      return cleanedData;
    },
    [allQuestions]
  );

  useEffect(() => {
    async function fetchAndSetAnswers() {
      let loadedData = {};
      if (currentUser && participantId && taskId) {
        try {
          const config: any = formConfig;
          const token = await currentUser.getIdToken();
          const res = await GetInstanceDetailsAsync(
            config.formId,
            participantId,
            Number(taskId),
            token
          );
          if (res?.data?.fields?.combinedFormFields) {
            loadedData = cleanLoadedData(res.data.fields.combinedFormFields);
          }
        } catch (err) {
          console.error("Failed to load form answers", err);
        }
      }

      const autoData: Record<string, any> = {};

      if (autoPopulateData) {
        const toCamelCase = (str: string) =>
          str
            .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
              index === 0 ? word.toLowerCase() : word.toUpperCase()
            )
            .replace(/\s+/g, "");

        // 1A. Handle waiverType object
        if (autoPopulateData.waiverType && typeof autoPopulateData.waiverType === 'object') {
          const waiverId = autoPopulateData.waiverType.id;
          if (waiverId) {
            const waiverQuestion = allQuestions.find(
              (q) => q.dynamicSource === "waiverTypes" || q.label.toLowerCase().includes("waiver")
            );
            if (waiverQuestion) {
              autoData[waiverQuestion.id] = waiverId.toString();
            }
          }
        }

        // 1B. Handle waiver object (legacy/alternative structure)
        if (autoPopulateData.waiver && typeof autoPopulateData.waiver === 'object') {
          const waiverName = autoPopulateData.waiver.waiverName;
          if (waiverName) {
            const waiverQuestion = allQuestions.find(
              (q) => q.dynamicSource === "waiverTypes" || q.label.toLowerCase().includes("waiver")
            );
            if (waiverQuestion) {
              autoData[waiverQuestion.id] = waiverName;
            }
          }
        }

        // 1C. NEW: Handle contactType object
        if (autoPopulateData.contactType && typeof autoPopulateData.contactType === 'object') {
          const contactTypeId = autoPopulateData.contactType.id;
          if (contactTypeId) {
            const contactTypeQuestion = allQuestions.find(
              (q) => q.dynamicSource === "taskContactTypes" || q.label.toLowerCase().includes("contact type")
            );
            if (contactTypeQuestion) {
              autoData[contactTypeQuestion.id] = contactTypeId.toString();
            }
          }
        }

        // 2. Iterate remaining keys
        for (const key in autoPopulateData) {
          if (Object.prototype.hasOwnProperty.call(autoPopulateData, key)) {

            // --- CRITICAL FIX: Skip all keys we handle manually ---
            if (key === "participantId" || key === "contactType" || key === "waiver" || key === "waiverType") {
              continue;
            }

            let question: any;

            if (key === "waiverType" || key === "waiverName") {
              question = allQuestions.find(
                (q) =>
                  q.dynamicSource === "waiverTypes" ||
                  q.label.toLowerCase().includes("waiver")
              );
            } else if (key === "scid") {
              question = allQuestions.find(
                (q) =>
                  q.id.toLowerCase().includes("scid") ||
                  q.label.toLowerCase().includes("scid") ||
                  q.label.toLowerCase().includes("support coordinator")
              );
            }

            if (!question) {
              question = allQuestions.find((q) => q.id === key);
            }
            if (!question) {
              question = allQuestions.find(
                (q) => toCamelCase(q.label) === key
              );
            }

            if (question) {
              const incomingValue = autoPopulateData[key];
              if ((question as any).isMulti && !Array.isArray(incomingValue)) {
                autoData[question.id] = incomingValue
                  ? [incomingValue.toString()]
                  : [];
              } else {
                autoData[question.id] = autoPopulateData[key];
              }
            }
            else if (key === "scid") {
              // Even if there is no visible question, we must save this 
              // so the PDF Mapper knows who the original SC was.
              autoData["scid"] = autoPopulateData[key];
            }
          }
        }
      }

      const finalData = { ...autoData, ...loadedData };
      setFormData(finalData);
    }

    fetchAndSetAnswers();
  }, [
    currentUser,
    formConfig,
    participantId,
    taskId,
    cleanLoadedData,
    autoPopulateData,
    allQuestions,
  ]);

  async function ClearFormData() {
    setFormData({});
  }

  return (
    <>
      {isSubmitting && (
        <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="text-white text-xl animate-pulse">Submitting...</div>
        </div>
      )}

      <div className="stepper_count_list">
        <VerticalStepBar
          steps={steps}
          currentStepIndex={currentStepIndex}
          errors={errors}
          onStepClick={(idx) => {
            setCurrentStepIndex(idx);
            closeSidebar();
          }}
        />
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={closeSidebar}
        ></div>
      )}

      <div className="form_stepper_right">
        <StepPanel
          step={steps[currentStepIndex]}
          currentStepIndex={currentStepIndex}
          totalSteps={steps.length}
          formData={formData}
          errors={errors}
          isSubmitting={isSubmitting}
          onChange={handleInputChange}
          onNext={handleNext}
          onPrev={handlePrev}
          dynamicOptions={dynamicOptions}
          clearForm={ClearFormData}
          formConfig={formConfig}
          participantId={participantId}
          autoPopulateData={autoPopulateData}
        />
      </div>
    </>
  );
};